import React, { Component } from 'react';
import { func, number, object, string } from 'prop-types';
import classNames from 'classnames';

import { intlShape } from '../../../util/reactIntl';
import {
  getStartHours,
  getEndHours,
  isInRange,
  isSameDate,
  isDayMomentInsideRange,
  timeOfDayFromLocalToTimeZone,
  timeOfDayFromTimeZoneToLocal,
  isDateSameOrAfter,
  findNextBoundary,
  timestampToDate,
  formatDateIntoPartials,
  monthIdString,
  getStartOf,
  initialVisibleMonth,
} from '../../../util/dates';
import { propTypes } from '../../../util/types';
import { bookingDateRequired } from '../../../util/validators';
import { FieldDateInput, FieldSelect, FieldTextInput, IconArrowHead } from '../../../components';

import css from './FieldDateAndTimeInput.module.css';
import moment from 'moment';

// dayCountAvailableForBooking is the maximum number of days forwards during which a booking can be made.
// This is limited due to Stripe holding funds up to 90 days from the
// moment they are charged:
// https://stripe.com/docs/connect/account-balances#holding-funds
//
// See also the API reference for querying time slots:
// https://www.sharetribe.com/api-reference/marketplace.html#query-time-slots

const TODAY = new Date();

const nextMonthFn = (currentMoment, timeZone) =>
  getStartOf(currentMoment, 'month', timeZone, 1, 'months');
const prevMonthFn = (currentMoment, timeZone) =>
  getStartOf(currentMoment, 'month', timeZone, -1, 'months');

const endOfRange = (date, dayCountAvailableForBooking, timeZone) => {
  return getStartOf(date, 'day', timeZone, dayCountAvailableForBooking - 1, 'days');
};

const getAvailableStartTimes = (intl, timeZone, bookingStart, timeSlotsOnSelectedDate, duration) => {
  if (timeSlotsOnSelectedDate?.length === 0 || !timeSlotsOnSelectedDate[0] || !bookingStart) {
    return [];
  }
  const bookingStartDate = getStartOf(bookingStart, 'day', timeZone);

  const allHours = timeSlotsOnSelectedDate.reduce((availableHours, t) => {
    const startDate = t.attributes.start;
    const endDate = t.attributes.end;
    const nextDate = getStartOf(bookingStartDate, 'day', timeZone, 1, 'days');

    // If the start date is after timeslot start, use the start date.
    // Otherwise use the timeslot start time.
    const startLimit = isDateSameOrAfter(bookingStartDate, startDate)
      ? bookingStartDate
      : startDate;

    // If date next to selected start date is inside timeslot use the next date to get the hours of full day.
    // Otherwise use the end of the timeslot.
    const endLimit = isDateSameOrAfter(endDate, nextDate) ? nextDate : endDate;

    const hours = getStartHours(startLimit, endLimit, timeZone, intl, duration);
    return availableHours.concat(hours);
  }, []);

  return allHours;
};

const getAvailableEndTimes = (
  intl,
  timeZone,
  bookingStartTime,
  bookingEndDate,
  selectedTimeSlot,
  duration,
) => {
  if (!selectedTimeSlot || !selectedTimeSlot.attributes || !bookingEndDate || !bookingStartTime) {
    return [];
  }

  const endDate = selectedTimeSlot.attributes.end;
  const bookingStartTimeAsDate = timestampToDate(bookingStartTime);

  const dayAfterBookingEnd = getStartOf(bookingEndDate, 'day', timeZone, 1, 'days');
  const dayAfterBookingStart = getStartOf(bookingStartTimeAsDate, 'day', timeZone, 1, 'days');
  const startOfEndDay = getStartOf(bookingEndDate, 'day', timeZone);

  let startLimit;
  let endLimit;

  if (!isDateSameOrAfter(startOfEndDay, bookingStartTimeAsDate)) {
    startLimit = bookingStartTimeAsDate;
    endLimit = isDateSameOrAfter(dayAfterBookingStart, endDate) ? endDate : dayAfterBookingStart;
  } else {
    // If the end date is on the same day as the selected booking start time
    // use the start time as limit. Otherwise use the start of the selected end date.
    startLimit = isDateSameOrAfter(bookingStartTimeAsDate, startOfEndDay)
      ? bookingStartTimeAsDate
      : startOfEndDay;

    // If the selected end date is on the same day as timeslot end, use the timeslot end.
    // Else use the start of the next day after selected date.
    endLimit = isSameDate(getStartOf(endDate, 'day', timeZone), startOfEndDay)
      ? endDate
      : dayAfterBookingEnd;
  }
  var data = getEndHours(startLimit, endLimit, timeZone, intl);
  if (duration) {
    var allowedendadate = new Date(startLimit).setHours(startLimit.getHours() + (Number(duration)));
    var allowedEndDates = data.filter(x => x.timestamp >= allowedendadate)
    return allowedEndDates;
  } else {
    return data
  }
}

const getTimeSlots = (timeSlots, date, timeZone) => {
  return timeSlots && timeSlots[0]
    ? timeSlots.filter(t => {
      return isInRange(date, t.attributes.start, t.attributes.end, 'day', timeZone);
    })
    : [];
};

// Use start date to calculate the first possible start time or times, end date and end time or times.
// If the selected value is passed to function it will be used instead of calculated value.

const getMonthlyTimeSlots = (monthlyTimeSlots, date, timeZone) => {
  const monthId = monthIdString(date, timeZone);

  return !monthlyTimeSlots || Object.keys(monthlyTimeSlots)?.length === 0
    ? []
    : monthlyTimeSlots[monthId] && monthlyTimeSlots[monthId].timeSlots
      ? monthlyTimeSlots[monthId].timeSlots
      : [];
};

// IconArrowHead component might not be defined if exposed directly to the file.
// This component is called before IconArrowHead component in components/index.js
const PrevIcon = props => (
  <IconArrowHead {...props} direction="left" rootClassName={css.arrowIcon} />
);
const NextIcon = props => (
  <IconArrowHead {...props} direction="right" rootClassName={css.arrowIcon} />
);

const Next = props => {
  const { currentMonth, dayCountAvailableForBooking, timeZone } = props;
  const nextMonthDate = nextMonthFn(currentMonth, timeZone);

  return isDateSameOrAfter(
    nextMonthDate,
    endOfRange(TODAY, dayCountAvailableForBooking, timeZone)
  ) ? null : (
    <NextIcon />
  );
};
const Prev = props => {
  const { currentMonth, timeZone } = props;
  const prevMonthDate = prevMonthFn(currentMonth, timeZone);
  const currentMonthDate = getStartOf(TODAY, 'month', timeZone);

  return isDateSameOrAfter(prevMonthDate, currentMonthDate) ? <PrevIcon /> : null;
};

const generateTimeOptions = (startTime, previousTime, timezone) => {

  const options = [];
  let currentTime = startTime ? moment.tz(parseInt(startTime), timezone) : moment.tz('', timezone);
  if (!startTime && currentTime._isValid) {
    currentTime.setHours(0, 0, 0, 0); // Set to 00:00 (midnight) if no date is provided
  }

  if(currentTime?._isValid) {

    for (let i = 0; i < 48; i++) { // 48 intervals of 30 minutes in 24 hours

      const timestamp = currentTime.valueOf();
      const timeOfDay = currentTime.format('hh:mm A');

      options.push({ timestamp, timeOfDay });

      // Move to the next 30-minute interval
      currentTime.minutes(currentTime.minutes() + 30);
      currentTime = moment.tz(currentTime.toISOString(), timezone);

      if(currentTime.format('hh:mm A') == '12:00 AM') {
        break;
      }
    }
  }
  return options;
}

/////////////////////////////////////
// FieldDateAndTimeInput component //
/////////////////////////////////////
class FieldDateAndTimeInput extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentMonth: getStartOf(TODAY, 'month', props.timeZone),
      serviceValue: '',
      serviceError: '',
      startPlaceholder: null,
      endPlaceholder: null,
    };

    this.fetchMonthData = this.fetchMonthData.bind(this);
    this.onMonthClick = this.onMonthClick.bind(this);
    this.onBookingStartDateChange = this.onBookingStartDateChange.bind(this);
    this.onBookingStartTimeChange = this.onBookingStartTimeChange.bind(this);
    this.onBookingEndDateChange = this.onBookingEndDateChange.bind(this);
    this.onBookingServiceChange = this.onBookingServiceChange.bind(this);
    this.isOutsideRange = this.isOutsideRange.bind(this);
    this.getAllTimeValues = this.getAllTimeValues.bind(this);
  }

  getAllTimeValues = (
    intl,
    timeZone,
    timeSlots,
    startDate,
    selectedStartTime,
    selectedEndDate,
    duration,
    noOfSevices,
    handleErrorChange,
  ) => {

    const startTimes = selectedStartTime
      ? []
      : getAvailableStartTimes(
        intl,
        timeZone,
        startDate,
        getTimeSlots(timeSlots, startDate, timeZone)
      );

    // Value selectedStartTime is a string when user has selected it through the form.
    // That's why we need to convert also the timestamp we use as a default
    // value to string for consistency. This is expected later when we
    // want to compare the sartTime and endTime.
    const startTime = selectedStartTime
      ? selectedStartTime
      : startTimes?.length > 0 && startTimes[0] && startTimes[0].timestamp
        ? startTimes[0].timestamp.toString()
        : null;

    const startTimeAsDate = startTime ? timestampToDate(startTime) : null;

    // Note: We need to remove 1ms from the calculated endDate so that if the end
    // date would be the next day at 00:00 the day in the form is still correct.
    // Because we are only using the date and not the exact time we can remove the
    // 1ms.
    const endDate = selectedEndDate
      ? selectedEndDate
      : startTimeAsDate
        ? new Date(findNextBoundary(startTimeAsDate, 'hour', timeZone).getTime() - 1)
        : null;

    const selectedTimeSlot = timeSlots.find(t =>
      isInRange(startTimeAsDate, t.attributes.start, t.attributes.end)
    );

    const endTimes = getAvailableEndTimes(intl, timeZone, startTime, endDate, selectedTimeSlot);

    // We need to convert the timestamp we use as a default value
    // for endTime to string for consistency. This is expected later when we
    // want to compare the sartTime and endTime.

    var endTime =
      endTimes?.length > 0 && endTimes[0] && endTimes[0]?.timestamp
        ? endTimes[0]?.timestamp?.toString()
        : null;

    if (duration) {
      const wholeNumber = Number.isInteger(Number(duration));
      const durations = Math.floor(duration);
      if (noOfSevices == '1') {
        wholeNumber ?
          endTime =
          endTimes?.length > 0 && endTimes[0] && endTimes[0]?.timestamp
            ? endTimes[(durations * 2) - 1]?.timestamp?.toString()
            : null
          : endTime =
          endTimes?.length > 0 && endTimes[0] && endTimes[0]?.timestamp
            ? endTimes[(Number(duration) * 2) - 1]?.timestamp?.toString()
            : null
        if (endTime == undefined) {
          this.setState({
            serviceError: 'Time slot booked. Choose another.'
          });
          handleErrorChange(true);
        } else {
          this.setState({
            serviceError: ''
          })
          handleErrorChange(false);
        }
      } else {
        wholeNumber ?
          endTime =
          endTimes?.length > 0 && endTimes[0] && endTimes[0]?.timestamp
            ? endTimes[(((durations * 2) - 1) * (Number(noOfSevices))) + (Number(noOfSevices) - 1)]?.timestamp?.toString()
            : null
          : endTime =
          endTimes?.length > 0 && endTimes[0] && endTimes[0]?.timestamp
            ? endTimes[((durations * 2) * (Number(noOfSevices))) + (Number(noOfSevices) - 1)]?.timestamp?.toString()
            : null
        if (endTime == undefined && noOfSevices != "" && noOfSevices != undefined) {
          this.setState({
            serviceError: 'Time slot booked. Choose another.'
          });
          handleErrorChange(true);
        } else {
          this.setState({
            serviceError: ''
          })
          handleErrorChange(false);
        }
      }

    }

    return { startTime, endDate, endTime, selectedTimeSlot };
  };

  fetchMonthData(date) {
    const { listingId, timeZone, onFetchTimeSlots, dayCountAvailableForBooking } = this.props;
    const endOfRangeDate = endOfRange(TODAY, dayCountAvailableForBooking, timeZone);

    // Don't fetch timeSlots for past months or too far in the future
    if (isInRange(date, TODAY, endOfRangeDate)) {
      // Use "today", if the first day of given month is in the past
      const start = isDateSameOrAfter(TODAY, date) ? TODAY : date;

      // Use endOfRangeDate, if the first day of the next month is too far in the future
      const nextMonthDate = nextMonthFn(date, timeZone);
      const end = isDateSameOrAfter(nextMonthDate, endOfRangeDate)
        ? getStartOf(endOfRangeDate, 'day', timeZone)
        : nextMonthDate;

      // Fetch time slots for given time range
      onFetchTimeSlots(listingId, start, end, timeZone);
    }
  }

  onMonthClick(monthFn) {
    const { onMonthChanged, timeZone } = this.props;

    this.setState(
      prevState => ({ currentMonth: monthFn(prevState.currentMonth, timeZone) }),
      () => {
        // Callback function after month has been updated.
        // react-dates component has next and previous months ready (but inivisible).
        // we try to populate those invisible months before user advances there.
        this.fetchMonthData(monthFn(this.state.currentMonth, timeZone));

        // If previous fetch for month data failed, try again.
        const monthId = monthIdString(this.state.currentMonth, timeZone);
        const currentMonthData = this.props.monthlyTimeSlots[monthId];
        if (currentMonthData && currentMonthData.fetchTimeSlotsError) {
          this.fetchMonthData(this.state.currentMonth);
        }

        // Call onMonthChanged function if it has been passed in among props.
        if (onMonthChanged) {
          onMonthChanged(monthId);
        }
      }
    );
  }

  onBookingStartDateChange = value => {
    const { monthlyTimeSlots, timeZone, intl, form, duration, handleErrorChange, listingType } = this.props;
    if (!value || !value.date) {
      form.batch(() => {
        form.change('bookingStartTime', null);
        form.change('bookingEndDate', { date: null });
        form.change('bookingEndTime', null);
      });
      this.setState({ currentMonth: getStartOf(TODAY, 'month', timeZone) });
      console.log('TODAY', TODAY);

      return;
    }

    // This callback function (onBookingStartDateChange) is called from react-dates component.
    // It gets raw value as a param - browser's local time instead of time in listing's timezone.
    const startDate = timeOfDayFromLocalToTimeZone(value.date, timeZone);
    const timeSlots = getMonthlyTimeSlots(monthlyTimeSlots, this.state.currentMonth, timeZone);
    const timeSlotsOnSelectedDate = getTimeSlots(timeSlots, startDate, timeZone);
    const noOfSevices = listingType == 'hourly-service' ? this.props?.values?.selectedHours : this.props?.values?.slot || '1';
    const selectedDuration = listingType == 'hourly-service' ? 1 : duration;

    const { startTime, endDate, endTime } = this.getAllTimeValues(
      intl,
      timeZone,
      timeSlotsOnSelectedDate,
      startDate,
      null,
      null,
      selectedDuration,
      noOfSevices,
      handleErrorChange,
    );

    
    // if (listingType != 'duration') {
    //   handleErrorChange(false);
    // }
      
    handleErrorChange(false);

    form.batch(() => {
      form.change('bookingStartTime', startTime);
      form.change('bookingEndDate', { date: endDate });
      form.change('bookingEndTime', endTime);
    });
  };

  onBookingStartTimeChange = value => {
    const { monthlyTimeSlots, timeZone, intl, form, values, listingType, duration, handleErrorChange, isModifyTime, transaction } = this.props;
    const timeSlots = getMonthlyTimeSlots(monthlyTimeSlots, this.state.currentMonth, timeZone);
    const startDate = values?.bookingStartDate?.date || new Date(parseInt(value));
    const timeSlotsOnSelectedDate = getTimeSlots(timeSlots, startDate, timeZone);
    const noOfSevices = listingType == 'hourly-service' ? this.props?.values?.selectedHours : this.props?.values?.slot || '1';
    const selectedDuration = listingType == 'hourly-service' ? 1 : duration;
    const { endDate, endTime } = this.getAllTimeValues(
      intl,
      timeZone,
      timeSlotsOnSelectedDate,
      startDate,
      value,
      null,
      selectedDuration,
      noOfSevices,
      handleErrorChange,
    );

    let updatedEndTime = endTime;
    if (endTime == null) {      
      const currentHour = startDate.getMinutes();
      const endDate =  moment.tz(startDate.getTime(), timeZone);
      endDate.minutes(endDate.minutes() + 30);
      updatedEndTime = moment.tz(endDate.valueOf(), timeZone);
    }

    form.batch(() => {
      form.change('bookingEndDate', { date: endDate });
      form.change('bookingEndTime', updatedEndTime._i || updatedEndTime);
      
    });

    if (isModifyTime && listingType == 'hourly-service') {
      const preStart = transaction.booking.attributes.displayStart;
      const preEnd = transaction.booking.attributes.displayEnd;
      const preHours = (preEnd - preStart) / (1000 * 60 * 60);

      const currentHour = moment.tz(startDate.getTime(), timeZone).hours();
      const endDate =  moment.tz(startDate.getTime(), timeZone);
      const endTime = endDate.hours(currentHour + preHours);

      form.batch(() => {
        form.change('bookingEndDate', { date: endDate });
        form.change('bookingEndTime', endTime.valueOf());
      });

      const endPlace = endDate.format('hh:mm A');

      this.setState({ endPlaceholder: endPlace });

    }
    if (isModifyTime && listingType == 'package') {
      const preStart = transaction.booking.attributes.displayStart;
      const preEnd = transaction.booking.attributes.displayEnd;

      const endDate = moment.tz(parseInt(startDate.getTime()), timeZone);
      const endTime = endDate.minutes(endDate.minutes() + 30);

      form.batch(() => {
        form.change('bookingEndDate', { date: endDate });
        form.change('bookingEndTime', endTime.valueOf());
      });

      const endPlace = endDate.format('hh:mm A');
      this.setState({ endPlaceholder: endPlace });

    }
  };

  onBookingEndDateChange = value => {
    const { monthlyTimeSlots, timeZone, intl, form, values } = this.props;
    if (!value || !value.date) {
      form.change('bookingEndTime', null);
      return;
    }

    // This callback function (onBookingStartDateChange) is called from react-dates component.
    // It gets raw value as a param - browser's local time instead of time in listing's timezone.
    const endDate = timeOfDayFromLocalToTimeZone(value.date, timeZone);

    const { bookingStartDate, bookingStartTime } = values;
    const startDate = bookingStartDate.date;
    const timeSlots = getMonthlyTimeSlots(monthlyTimeSlots, this.state.currentMonth, timeZone);
    const timeSlotsOnSelectedDate = getTimeSlots(timeSlots, startDate, timeZone);

    const { endTime } = this.getAllTimeValues(
      intl,
      timeZone,
      timeSlotsOnSelectedDate,
      startDate,
      bookingStartTime,
      endDate
    );

    form.change('bookingEndTime', endTime);
  };

  onBookingServiceChange = value => {

    const { monthlyTimeSlots, timeZone, intl, form, values, duration, handleErrorChange, listingType, minHours } = this.props;
    if (value && value >= minHours) {
      value = { slot: value };
    }
    else {
      value = { slot: values.selectedHours }
    }

    const selectedDuration = listingType == 'hourly-service' ? 1 : duration;
    if (!value || !value.slot) {
      form.batch(() => {
        form.change('slot', null);
      });
    }

    const noOfSevices = value?.slot;
    if (noOfSevices == "") {
      this.setState({
        serviceError: '',
      })
    }
    const { bookingStartDate, bookingStartTime } = values;
    const startDate = bookingStartDate.date;
    const timeSlots = getMonthlyTimeSlots(monthlyTimeSlots, this.state.currentMonth, timeZone);
    const timeSlotsOnSelectedDate = getTimeSlots(timeSlots, startDate, timeZone);


    const { endDate, endTime } = this.getAllTimeValues(
      intl,
      timeZone,
      timeSlotsOnSelectedDate,
      startDate,
      bookingStartTime,
      null,
      selectedDuration,
      noOfSevices,
      handleErrorChange,
    );
    form.batch(() => {
      form.change('bookingEndDate', { date: endDate });
      form.change('bookingEndTime', endTime);
      form.change('slot', noOfSevices);
    });

  }

  isOutsideRange(day, bookingStartDate, selectedTimeSlot, timeZone) {
    if (!selectedTimeSlot) {
      return true;
    }

    // 'day' is pointing to browser's local time-zone (react-dates gives these).
    // However, bookingStartDate and selectedTimeSlot refer to times in listing's timeZone.
    const localizedDay = timeOfDayFromLocalToTimeZone(day, timeZone);
    // Given day (endDate) should be after the start of the day of selected booking start date.
    const startDate = getStartOf(bookingStartDate, 'day', timeZone);
    // 00:00 would return wrong day as the end date.
    // Removing 1 millisecond, solves the exclusivity issue.
    const inclusiveEnd = new Date(selectedTimeSlot.attributes.end.getTime() - 1);
    // Given day (endDate) should be before the "next" day of selected timeSlots end.
    const endDate = getStartOf(inclusiveEnd, 'day', timeZone, 1, 'days');
    return !(
      isDateSameOrAfter(localizedDay, startDate) && isDateSameOrAfter(endDate, localizedDay)
    );
  }

  componentDidUpdate(prevProps) {
    const { values, monthlyTimeSlots, timeZone, duration, intl } = this.props;
    const bookingStartDate = values.bookingStartDate?.date || null;

    if (bookingStartDate !== prevProps.values.bookingStartDate?.date) {
      const timeSlotsOnSelectedMonth = getMonthlyTimeSlots(
        monthlyTimeSlots,
        this.state.currentMonth,
        timeZone
      );

      const timeSlotsOnSelectedDate = getTimeSlots(
        timeSlotsOnSelectedMonth,
        bookingStartDate,
        timeZone
      );

      const availableStartTimes = getAvailableStartTimes(
        intl,
        timeZone,
        bookingStartDate,
        timeSlotsOnSelectedDate,
        duration
      );

      if (availableStartTimes.length === 0 && bookingStartDate != null) {
        this.setState({
          serviceError: 'Not enough slots available on selected date.'
        });
      }
    }
  }

  render() {
    const {
      rootClassName,
      className,
      formId,
      startDateInputProps,
      // endDateInputProps,
      values,
      monthlyTimeSlots,
      timeZone,
      intl,
      dayCountAvailableForBooking,
      duration,
      listingType,
      handleErrorChange,
      fetchLineItemsInProgress,
      isModifyTime,
      transaction,
      handleDecrement,
      handleIncrement,
      hours,
      selectedSlots,
      handleSlotIncrement,
      handleSlotDecrement,
      updateIsValid,
      previousValidState
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);

    let bookingStartDate =
      values.bookingStartDate && values.bookingStartDate.date ? values.bookingStartDate.date : null;
    if (isModifyTime) {
      bookingStartDate = transaction?.booking?.attributes?.displayStart;
    }
    const bookingStartTime = values.bookingStartTime ? values.bookingStartTime : null;
    const bookingEndDate =
      values.bookingEndDate && values.bookingEndDate.date ? values.bookingEndDate.date : null;

    // let  selectedMonth: getStartOf(TODAY, 'month', props.timeZone);
    const timeSlotsOnSelectedMonth = getMonthlyTimeSlots(
      monthlyTimeSlots,
      this.state.currentMonth,
      timeZone
    );
    const timeSlotsOnSelectedDate = getTimeSlots(
      timeSlotsOnSelectedMonth,
      bookingStartDate,
      timeZone
    );

    const availableStartTimes = getAvailableStartTimes(
      intl,
      timeZone,
      bookingStartDate,
      timeSlotsOnSelectedDate,
      duration,
    );

    const firstAvailableStartTime =
      availableStartTimes?.length > 0 && availableStartTimes[0] && availableStartTimes[0].timestamp
        ? availableStartTimes[0].timestamp
        : null;

    const { startTime, endDate, selectedTimeSlot } = this.getAllTimeValues(
      intl,
      timeZone,
      timeSlotsOnSelectedDate,
      bookingStartDate,
      bookingStartTime || firstAvailableStartTime,
      bookingEndDate || bookingStartDate
    );

    const availableEndTimes = getAvailableEndTimes(
      intl,
      timeZone,
      bookingStartTime || startTime,
      bookingEndDate || endDate,
      selectedTimeSlot,
      duration,
    );

    const isDayBlocked = timeSlotsOnSelectedMonth
      ? day =>
        !timeSlotsOnSelectedMonth.find(timeSlot =>
          isDayMomentInsideRange(
            day,
            timeSlot.attributes.start,
            timeSlot.attributes.end,
            timeZone
          )
        )
      : () => false;

    const nextBoundary = findNextBoundary(TODAY, 'hour', timeZone);
    let placeholderTime = '08:00';
    try {
      placeholderTime = formatDateIntoPartials(nextBoundary, intl, { timeZone })?.time;
    } catch (error) {
      // No need to handle error
    }

    const startOfToday = getStartOf(TODAY, 'day', timeZone);
    const bookingEndTimeAvailable = bookingStartDate && (bookingStartTime || startTime);


    if (isModifyTime) {

      var displayDay=moment(bookingStartDate).tz(timeZone).format('ddd DD MMM YYYY');
      // let displayDay = bookingStartDate.toLocaleDateString('en-GB', {
      //   weekday: 'short',   // Sat
      //   day: '2-digit',     // 12
      //   month: 'short',     // Oct
      //   year: 'numeric'     // 2024
      // });
      // displayDay = displayDay.replace(/,/g, '');

      let placeholderStart = transaction?.booking?.attributes?.displayStart;
      placeholderStart = placeholderStart.getTime();
      let placeholderEnd = transaction?.booking?.attributes?.displayEnd;

      placeholderEnd = moment.tz(placeholderEnd, timeZone);
      let placeHolderEndValue = placeholderEnd.valueOf();
      placeholderEnd = listingType == 'duration' ? placeholderEnd.format('hh:mm A') : placeholderEnd.format('hh:mm A');

      const start = moment.tz(transaction.booking.attributes.displayStart.getTime(), timeZone);
      const end = moment.tz(transaction.booking.attributes.displayEnd.getTime(), timeZone);
      
      const timeOptions = generateTimeOptions(start._i, transaction.booking.attributes.displayStart, timeZone);
      
      let timeOptionsEnd = generateTimeOptions(values?.bookingStartTime || end._i, transaction.booking.attributes.displayStart, timeZone);

      if(startTime) {

        const selectedDate = new Date(parseInt(startTime)).getDate();
        const filteredOptions = timeOptionsEnd
        .sort((a, b) => a.timestamp - b.timestamp)  // Sort by timestamp
        .filter(option => new Date(option.timestamp).getDate() <= selectedDate);  // Keep options before or equal to 11:30 PM
      
        timeOptionsEnd = filteredOptions;
      }

      let isValid = false;
      function dateToMinutes(date) {
        return date.getHours() * 60 + date.getMinutes();
      }

      let bookingCondition =
        listingType == 'duration'
          ? values?.bookingStartTime &&
            values?.bookingEndTime &&
            (new Date(placeholderStart).toISOString() !==
              new Date(parseInt(values?.bookingStartTime)).toISOString() ||
              placeholderEnd !== values?.bookingEndTime)
          : values.bookingStartTime &&
            values.bookingEndTime &&
            placeholderStart !== values.bookingStartTime &&
            placeholderEnd !== values.bookingEndTime;

      if (bookingCondition) {
        const bookingStartDate = moment.tz(parseInt(values.bookingStartTime), timeZone);
        const bookingEndDate = moment.tz(parseInt(values.bookingEndTime), timeZone) || values.bookingEndTime;
        const newStart = bookingStartDate.hours() * 60;
        const newEnd = bookingEndDate?.hours() * 60;
        const displayStart = moment.tz(transaction.booking.attributes.displayStart.getTime(), timeZone);
        const displayEnd = moment.tz(transaction.booking.attributes.displayEnd.getTime(), timeZone);
        let isTimeValid = false;
        // Check if the booking times fall within any of the available time slots
        const isBookingAvailable = Object.keys(monthlyTimeSlots).some(monthKey => {
          // Get the time slots for the dynamic month key
          const availableTimeSlots = monthlyTimeSlots[monthKey].timeSlots;
          const timeRanges = availableTimeSlots?.map(timeSlot => {
            const timeSlotStart = moment.tz(timeSlot.attributes.start.getTime(), timeZone); // Convert start to Date
            const timeSlotEnd = moment.tz(timeSlot.attributes.end.getTime(), timeZone); // Convert end to Date
            return { start: timeSlotStart, end: timeSlotEnd }; // Return the transformed object
          });
          // const timeRanges2 = [
          //   { start: new Date('2025-08-01T00:30:00'), end: new Date('2025-08-01T04:00:00') },
          //   { start: new Date('2025-08-01T04:00:00'), end: new Date('2025-08-01T06:00:00') },
          //   { start: new Date('2025-08-01T06:00:00'), end: new Date('2025-08-01T10:00:00') },
          //   { start: new Date('2025-08-01T12:00:00'), end: new Date('2025-08-01T23:00:00') }
          // ];

          if (displayStart && displayEnd) {
            const displayStartDate = moment.tz(displayStart._i, timeZone);
            const displayEndDate = moment.tz(displayEnd._i, timeZone);
            timeRanges?.push({ start: displayStartDate, end: displayEndDate });
          }

          // Check if the new slot fits between two or more time ranges, allowing for boundary alignment
          // Check if the new slot fits within the given time ranges
          timeRanges?.sort((a, b) => new Date(a.start) - new Date(b.start))
          // Check if the new slot fits across consecutive time ranges with no more than a 30-minute gap

          let currentStart = newStart;

          for (let i = 0; i < timeRanges?.length; i++) {
            const rangeStart = timeRanges[i].start.hours() * 60;
            const rangeEnd = timeRanges[i].end.hours() * 60;

            // If the current start is within the range or matches exactly
            if (currentStart >= rangeStart && currentStart <= rangeEnd) {
              // Check if the end of the slot falls within this range
              if (newEnd <= rangeEnd) {
                isTimeValid = true;
                break;
              } else {
                // Move to the next range, allowing a 30-minute buffer between ranges
                if (i + 1 < timeRanges?.length) {
                  const nextRangeStart = timeRanges[i + 1].start.hours() * 60;
                  if (nextRangeStart - rangeEnd <= 30) {
                    currentStart = rangeEnd;  // Extend the search to the next range start
                  } else {
                    break;
                  }
                }
              }
            }
          }
        });

        isValid = isTimeValid;
      }

      if (isValid != previousValidState) {
        updateIsValid(isValid);
      }
      
      return (
        <div className={classes}>
          <div className={css.displayDay}>
            <h4>{displayDay}</h4>
          </div>
          <div className={listingType == 'package' ? css.formRowCenter : css.formRow}>
            <div className={css.field}>
              <FieldSelect
                name="bookingStartTime"
                id={formId ? `${formId}.bookingStartTime` : 'bookingStartTime'}
                className={css.fieldSelect}
                selectClassName={css.select}
                label={listingType == 'package' ? 'Ready Time' : intl.formatMessage({ id: 'FieldDateAndTimeInput.startTime' })}
                // disabled={!bookingStartDate}
                onChange={this.onBookingStartTimeChange}
                defaultValue={placeholderStart}
              >
                {timeOptions?.map(p => (
                  <option key={p.timeOfDay} value={p.timestamp}>
                    {p.timeOfDay}
                  </option>
                ))}
              </FieldSelect>
            </div>

            {listingType == 'package' ? null
              :
              <>

                <div className={css.lineBetween}>-</div>

                <div className={css.field}>
                  <FieldSelect
                    name="bookingEndTime"
                    id={formId ? `${formId}.bookingEndTime` : 'bookingEndTime'}
                    className={css.fieldSelect}
                    selectClassName={css.select}
                    label={intl.formatMessage({ id: 'FieldDateAndTimeInput.endTime' })}
                    disabled={!bookingEndTimeAvailable}
                    defaultValue={listingType != 'hourly-service' ? placeHolderEndValue : null}
                  >
                    {bookingEndTimeAvailable && listingType != 'hourly-service' ? (
                      
                      timeOptionsEnd?.map((p, index) => {
                        return(
                        index == 0 ? null :
                          <option key={p.timeOfDay === '00:00' ? '24:00' : p.timeOfDay} value={p.timestamp}>
                            {p.timeOfDay === '00:00' ? '24:00' : p.timeOfDay}
                          </option>
                      )})
                    ) : (
                      <option>{this.state.endPlaceholder || placeholderEnd || placeholderTime}</option>
                    )}
                  </FieldSelect>
                </div>
              </>
            }
          </div>
          {/* {listingType == 'duration' || listingType =='hourly-service'? 
            <span className={css.serviceValidation}>{this.state.serviceError}</span>
          : null} */}
        </div>
      );
    }
    return (
      <div className={classes}>
        <div className={css.formRow}>
          <div className={classNames(css.field, css.startDate)}>
            <FieldDateInput
              className={css.fieldDateInput}
              name="bookingStartDate"
              id={formId ? `${formId}.bookingStartDate` : 'bookingStartDate'}
              label={startDateInputProps.label}
              placeholderText={startDateInputProps.placeholderText}
              format={v =>
                v && v.date ? { date: timeOfDayFromTimeZoneToLocal(v.date, timeZone) } : v
              }
              parse={v =>
                v && v.date ? { date: timeOfDayFromLocalToTimeZone(v.date, timeZone) } : v
              }
              initialVisibleMonth={initialVisibleMonth(bookingStartDate || startOfToday, timeZone)}
              isDayBlocked={isDayBlocked}
              onChange={this.onBookingStartDateChange}
              onPrevMonthClick={() => this.onMonthClick(prevMonthFn)}
              onNextMonthClick={() => this.onMonthClick(nextMonthFn)}
              navNext={
                <Next
                  currentMonth={this.state.currentMonth}
                  timeZone={timeZone}
                  dayCountAvailableForBooking={dayCountAvailableForBooking}
                />
              }
              navPrev={<Prev currentMonth={this.state.currentMonth} timeZone={timeZone} />}
              useMobileMargins
              validate={bookingDateRequired(
                intl.formatMessage({ id: 'BookingTimeForm.requiredDate' })
              )}
              onClose={event =>
                this.setState({
                  currentMonth: getStartOf(event?.date ?? TODAY, 'month', this.props.timeZone),
                })
              }
            />
          </div>
        </div>
        <div className={css.formRow1}>
          <div className={css.servicesBlock}>
            <div className={css.field}>
              {listingType == 'package' ? (
                <FieldSelect
                  name="bookingStartTime"
                  id={formId ? `${formId}.bookingStartTime` : 'bookingStartTime'}
                  className={bookingStartDate ? css.fieldSelect : css.fieldSelectDisabled}
                  selectClassName={bookingStartDate ? css.select : css.selectDisabled}
                  label={intl.formatMessage({ id: 'FieldDateAndTimeInput.packageTime' })}
                  disabled={!bookingStartDate}
                  onChange={this.onBookingStartTimeChange}
                >
                  {bookingStartDate ? (
                    availableStartTimes?.map(p => (
                      <option key={p.timeOfDay} value={p.timestamp}>
                        {p.timeOfDay}
                      </option>
                    ))
                  ) : (
                    <option>{placeholderTime}</option>
                  )}
                </FieldSelect>
              ) : (
                <FieldSelect
                  name="bookingStartTime"
                  id={formId ? `${formId}.bookingStartTime` : 'bookingStartTime'}
                  className={bookingStartDate ? css.fieldSelect : css.fieldSelectDisabled}
                  selectClassName={bookingStartDate ? css.select : css.selectDisabled}
                  label={intl.formatMessage({ id: 'FieldDateAndTimeInput.startTime' })}
                  disabled={!bookingStartDate}
                  onChange={this.onBookingStartTimeChange}
                >
                  {bookingStartDate ? (
                    availableStartTimes?.map(p => (
                      <option key={p.timeOfDay} value={p.timestamp}>
                        {p.timeOfDay}
                      </option>
                    ))
                  ) : (
                    <option>{placeholderTime}</option>
                  )}
                </FieldSelect>
              )}
            </div>
            {listingType == 'hourly-service' ? (

              <div>
                <label style={{ marginLeft: '25px' }}>No of hours</label>
                <div className={css.numberAdjusterContainer}>
                  <button
                    type="button"
                    className={css.adjustButton}
                    onClick={() => {
                      handleDecrement();
                      this.onBookingServiceChange(hours - 1);
                    }}
                    disabled={!bookingStartDate || fetchLineItemsInProgress}
                  >
                    -
                  </button>

                  <input className={css.adjustInput} type="text" value={hours} readOnly />

                  <button
                    type="button"
                    className={css.adjustButton}
                    onClick={() => {
                      handleIncrement();
                      this.onBookingServiceChange(hours + 1);
                    }}
                    disabled={!bookingStartDate || fetchLineItemsInProgress}
                  >
                    +
                  </button>
                </div>
              </div>

            ) : null}

            {listingType == 'duration' ? (
              <>

                <>
                  <div>
                    <label style={{ marginLeft: '25px' }}>No of Services</label>

                    <div className={css.numberAdjusterContainer}>

                      <button
                        type="button"
                        className={css.adjustButton}
                        onClick={() => {
                          handleSlotDecrement();
                          this.onBookingServiceChange(selectedSlots - 1);
                        }}
                        disabled={!bookingStartDate || fetchLineItemsInProgress}
                      // onClick={handleSlotDecrement}
                      >
                        -
                      </button>

                      <input className={css.adjustInput} type="text" value={selectedSlots} readOnly />

                      <button
                        type="button"
                        className={css.adjustButton}
                        onClick={() => {
                          const slots = selectedSlots + 1;
                          handleSlotIncrement();
                          this.onBookingServiceChange(selectedSlots + 1);
                        }}
                        disabled={!bookingStartDate || fetchLineItemsInProgress}
                      >
                        +
                      </button>
                      <br></br>
                    </div>
                  </div>
                </>

                {/* <div className={css.lineBetween} >&nbsp;</div>
          <div className={css.field}>
          <FieldTextInput
            id = {formId ? `${formId}.slot` : 'slot'}
            name = 'slot'
            label={intl.formatMessage({ id: 'FieldDateAndTimeInput.noOfService' })}
            placeholder = '1'
            disabled={!bookingStartDate || fetchLineItemsInProgress}
            onChange = {this.onBookingServiceChange}
          />
          </div> */}
              </>
            ) : (
              <>
                {listingType == 'package' || listingType == 'hourly-service' ? null : (
                  <div className={bookingStartDate ? css.lineBetween : css.lineBetweenDisabled}>
                    -
                  </div>
                )}

                {listingType == 'package' || listingType == 'hourly-service' ? null : (
                  <div className={css.field}>
                    <FieldSelect
                      name="bookingEndTime"
                      id={formId ? `${formId}.bookingEndTime` : 'bookingEndTime'}
                      className={bookingStartDate ? css.fieldSelect : css.fieldSelectDisabled}
                      selectClassName={bookingStartDate ? css.select : css.selectDisabled}
                      label={intl.formatMessage({ id: 'FieldDateAndTimeInput.endTime' })}
                      disabled={!bookingEndTimeAvailable}
                    >
                      {bookingEndTimeAvailable ? (
                        availableEndTimes?.map(p => (
                          <option
                            key={p.timeOfDay === '00:00' ? '24:00' : p.timeOfDay}
                            value={p.timestamp}
                          >
                            {p.timeOfDay === '00:00' ? '24:00' : p.timeOfDay}
                          </option>
                        ))
                      ) : (
                        <option>{placeholderTime}</option>
                      )}
                    </FieldSelect>
                  </div>
                )}
              </>
            )}
          </div>
          {(listingType == 'duration' || listingType == 'hourly-service') && this.state.serviceError != '' ?
            <div className={css.serviceValidation}>{this.state.serviceError}</div>
            : null}
        </div>
      </div>
    );
  }
}

FieldDateAndTimeInput.defaultProps = {
  rootClassName: null,
  className: null,
  startDateInputProps: null,
  endDateInputProps: null,
  startTimeInputProps: null,
  endTimeInputProps: null,
  listingId: null,
  monthlyTimeSlots: null,
  timeZone: null,
};

FieldDateAndTimeInput.propTypes = {
  rootClassName: string,
  className: string,
  formId: string,
  startDateInputProps: object,
  endDateInputProps: object,
  startTimeInputProps: object,
  endTimeInputProps: object,
  form: object.isRequired,
  values: object.isRequired,
  listingId: propTypes.uuid,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,
  timeZone: string,
  dayCountAvailableForBooking: number,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default FieldDateAndTimeInput;
